import { useEffect, useRef } from 'react';
import type { NextPage } from 'next';
import {
  Box,
  Divider,
  Heading,
  Link,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import Hls from 'hls.js';
import NextHead from 'next/head';
import IntellectualPropertyGuideline from '../../components/IntellectualPropertyGuideline';
import {
  URL_COPYRIGHT_INFRINGEMENT_FORM,
  URL_IP_GUIDELINE_VIDEO,
} from '../../common/constants';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { title } from '../../common/libs/metadata';
import { getPublicRuntimeConfig } from '../../common/config';

export { getServerSideProps } from '../../server/serverSideProps/emptyWithAuth';

function GuidelineVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current!;
    video.addEventListener('play', () => {
      sendAnalyticsEvent(
        'ip_guideline',
        'play',
        `${Math.floor(video.currentTime)}`,
      );
    });
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(URL_IP_GUIDELINE_VIDEO);
      hls.attachMedia(video);
      return () => {
        hls.removeAllListeners();
        hls.stopLoad();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = URL_IP_GUIDELINE_VIDEO;
    }
  }, []);

  return <video ref={videoRef} controls></video>;
}

const IntellectualPropertyGuidelinePage: NextPage = () => {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}/docs/ip-guideline`;

  return (
    <>
      <NextHead>
        <title>{title('Antaa Slide知的財産取扱いガイドライン')}</title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <Box>
        <UnorderedList>
          <ListItem>
            <Link href="#guideline">知的財産取扱いガイドライン</Link>
          </ListItem>
          <ListItem>
            <Link href="#video">解説動画</Link>
          </ListItem>
          <ListItem>
            <Link href="#report">権利侵害のご報告</Link>
          </ListItem>
        </UnorderedList>
        <Divider mt={10} />
        <Box>
          <Box id="guideline" pb={100} />
          <IntellectualPropertyGuideline />
        </Box>
        <Box>
          <Box id="video" pb={100} />
          <Heading as="h2">解説動画</Heading>
          <Text mt={8}>
            スライド作成時に知っておきたい著作権を中心とした一般的な注意事項について、菅沼弁護士に分かりやすく解説していただきます。
            <br />
            また2022年6月に公表した「Antaa
            Slide知的財産取扱いガイドライン」についても概要的な解説していただきます。
          </Text>
          <Box mt={4}>
            <GuidelineVideo />
          </Box>
        </Box>
        <Box>
          <Box id="report" pb={100} />
          <Heading as="h2">権利侵害のご報告</Heading>
          <Text mt={8}>
            権利侵害のご報告は
            <Link href={URL_COPYRIGHT_INFRINGEMENT_FORM} isExternal>
              こちらのフォーム
            </Link>
            よりご連絡ください。
          </Text>
        </Box>
      </Box>
    </>
  );
};

export default IntellectualPropertyGuidelinePage;
